'use client';

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Row,
  Col,
  Table,
  Typography,
  Tag,
  Tabs,
  Button,
  Input,
  Select,
  Space,
  Progress,
  Alert,
  Card,
} from 'antd';
import {
  ShoppingOutlined,
  InboxOutlined,
  DatabaseOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  ReloadOutlined,
  BranchesOutlined,
  SafetyCertificateOutlined,
  FilterOutlined,
  PrinterOutlined,
} from '@ant-design/icons';
import { useRouter } from 'next/navigation';
import { PASTEL_THEME, PastelStatusBadge } from '@/components/PastelBadge';
import {
  DashboardKPIs,
  OutstandingPOItemReport,
  TraceabilityLedgerRecord,
  SupplierPerformanceReport,
} from '@/services/reportService';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text } = Typography;

export default function ReportsPage() {
  const router = useRouter();
  const [activeTab, setActiveTab] = useState<string>('outstanding-pos');
  const [loading, setLoading] = useState<boolean>(true);

  // Report datasets
  const [kpis, setKpis] = useState<DashboardKPIs | null>(null);
  const [outstandingPOs, setOutstandingPOs] = useState<OutstandingPOItemReport[]>([]);
  const [traceability, setTraceability] = useState<TraceabilityLedgerRecord[]>([]);
  const [supplierQuality, setSupplierQuality] = useState<SupplierPerformanceReport[]>([]);

  // Filters
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [supplierFilter, setSupplierFilter] = useState<string>('ALL');
  const [stageFilter, setStageFilter] = useState<string>('ALL');

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/reports');
      const data = await res.json();
      if (data.success && data.data) {
        setKpis(data.data.kpis);
        setOutstandingPOs(data.data.outstandingPOs);
        setTraceability(data.data.traceability);
        setSupplierQuality(data.data.supplierQuality);
      }
    } catch {
      // ignore fetch errors
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let ignore = false;
    async function load() {
      try {
        const res = await fetch('/api/reports');
        const data = await res.json();
        if (!ignore && data.success && data.data) {
          setKpis(data.data.kpis);
          setOutstandingPOs(data.data.outstandingPOs);
          setTraceability(data.data.traceability);
          setSupplierQuality(data.data.supplierQuality);
        }
      } catch {
        // ignore fetch errors
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    load();
    return () => {
      ignore = true;
    };
  }, []);

  // Outstanding POs filtering
  const filteredOutstanding = useMemo(() => {
    return outstandingPOs.filter((item) => {
      const matchesSearch =
        item.poNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.supplierName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.itemCode.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.itemName.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesSupplier =
        supplierFilter === 'ALL' || item.supplierId === supplierFilter;

      return matchesSearch && matchesSupplier;
    });
  }, [outstandingPOs, searchQuery, supplierFilter]);

  // Traceability filtering
  const filteredTraceability = useMemo(() => {
    return traceability.filter((rec) => {
      const matchesSearch =
        rec.prNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (rec.poNumber && rec.poNumber.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (rec.grNumber && rec.grNumber.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (rec.costLayerId && rec.costLayerId.toLowerCase().includes(searchQuery.toLowerCase())) ||
        rec.itemCode.toLowerCase().includes(searchQuery.toLowerCase()) ||
        rec.itemName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        rec.requesterName.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStage = stageFilter === 'ALL' || rec.pipelineStage === stageFilter;

      return matchesSearch && matchesStage;
    });
  }, [traceability, searchQuery, stageFilter]);

  // Supplier performance filtering
  const filteredSuppliers = useMemo(() => {
    return supplierQuality.filter((s) => {
      return (
        supplierFilter === 'ALL' || s.supplierId === supplierFilter ||
        s.supplierName.toLowerCase().includes(searchQuery.toLowerCase())
      );
    });
  }, [supplierQuality, searchQuery, supplierFilter]);

  // Outstanding summary totals
  const totalOutstandingValue = useMemo(() => {
    return filteredOutstanding.reduce((sum, item) => sum + item.outstandingValue, 0);
  }, [filteredOutstanding]);

  // Outstanding PO Columns
  const outstandingColumns: ColumnsType<OutstandingPOItemReport> = [
    {
      title: 'PO Number',
      dataIndex: 'poNumber',
      key: 'poNumber',
      width: 140,
      render: (poNumber: string, rec) => (
        <div>
          <a
            onClick={() => router.push(`/purchase-orders/${rec.poId}`)}
            style={{ fontWeight: 700, color: '#4F46E5' }}
          >
            {poNumber}
          </a>
          <div style={{ fontSize: 11, color: '#64748B' }}>
            {new Date(rec.poDate).toLocaleDateString('id-ID', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })}
          </div>
          {rec.originPRNumber && (
            <Tag color="cyan" style={{ fontSize: 10, marginTop: 2, padding: '0 4px', lineHeight: '16px' }}>
              via {rec.originPRNumber}
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Supplier',
      dataIndex: 'supplierName',
      key: 'supplierName',
      width: 200,
      render: (name: string, rec) => (
        <div>
          <div style={{ fontWeight: 600, color: '#1E293B' }}>{name}</div>
          <div style={{ fontSize: 11, color: '#64748B' }}>By: {rec.purchaserName}</div>
        </div>
      ),
    },
    {
      title: 'Item Catalog & Specs',
      key: 'item',
      render: (_, rec) => (
        <div>
          <div style={{ fontWeight: 600, color: '#0F172A' }}>{rec.itemName}</div>
          <Space orientation="horizontal" size={4}>
            <Tag color="purple" style={{ fontSize: 11 }}>
              {rec.itemCode}
            </Tag>
            <Tag style={{ fontSize: 11 }}>{rec.uomCode}</Tag>
          </Space>
        </div>
      ),
    },
    {
      title: 'Quantities',
      key: 'quantities',
      width: 180,
      render: (_, rec) => (
        <div>
          <div style={{ fontSize: 12, display: 'flex', justifyContent: 'space-between' }}>
            <Text type="secondary">Ordered:</Text>
            <Text strong>{rec.orderedQty.toLocaleString('id-ID')} {rec.uomCode}</Text>
          </div>
          <div style={{ fontSize: 12, display: 'flex', justifyContent: 'space-between' }}>
            <Text type="secondary">Received:</Text>
            <Text style={{ color: '#16A34A', fontWeight: 600 }}>
              {rec.receivedQty.toLocaleString('id-ID')} {rec.uomCode}
            </Text>
          </div>
          <div style={{ fontSize: 12, display: 'flex', justifyContent: 'space-between' }}>
            <Text type="secondary">Outstanding:</Text>
            <Text style={{ color: rec.outstandingQty > 0 ? '#E11D48' : '#64748B', fontWeight: 700 }}>
              {rec.outstandingQty.toLocaleString('id-ID')} {rec.uomCode}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Fulfillment Status',
      key: 'fulfillment',
      width: 170,
      render: (_, rec) => (
        <div style={{ width: 140 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
            <PastelStatusBadge status={rec.poStatus} size="small" />
            <Text style={{ fontSize: 12, fontWeight: 700 }}>{rec.fulfillmentPercent}%</Text>
          </div>
          <Progress
            percent={rec.fulfillmentPercent}
            size="small"
            status={rec.fulfillmentPercent === 100 ? 'success' : 'active'}
            strokeColor={rec.fulfillmentPercent === 100 ? '#10B981' : '#6366F1'}
          />
        </div>
      ),
    },
    {
      title: 'Outstanding Value',
      key: 'value',
      align: 'right',
      width: 160,
      render: (_, rec) => (
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontWeight: 800, fontSize: 14, color: rec.outstandingValue > 0 ? '#B91C1C' : '#15803D' }}>
            Rp {rec.outstandingValue.toLocaleString('id-ID')}
          </div>
          <div style={{ fontSize: 11, color: '#64748B' }}>
            @ Rp {rec.unitPrice.toLocaleString('id-ID')} / {rec.uomCode}
          </div>
        </div>
      ),
    },
  ];

  // Traceability Ledger Columns
  const traceabilityColumns: ColumnsType<TraceabilityLedgerRecord> = [
    {
      title: 'Pipeline Stage',
      dataIndex: 'pipelineStage',
      key: 'pipelineStage',
      width: 160,
      render: (stage: string) => {
        switch (stage) {
          case 'COSTED_INVENTORY':
            return (
              <Tag
                color="green"
                style={{
                  padding: '4px 10px',
                  borderRadius: 12,
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <DatabaseOutlined /> Stock Costed
              </Tag>
            );
          case 'GR_STAGE':
            return (
              <Tag
                color="blue"
                style={{
                  padding: '4px 10px',
                  borderRadius: 12,
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <InboxOutlined /> Received (GR)
              </Tag>
            );
          case 'PO_STAGE':
            return (
              <Tag
                color="orange"
                style={{
                  padding: '4px 10px',
                  borderRadius: 12,
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <ShoppingOutlined /> Issued (PO)
              </Tag>
            );
          default:
            return (
              <Tag
                color="default"
                style={{
                  padding: '4px 10px',
                  borderRadius: 12,
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <FileTextOutlined /> Requisition (PR)
              </Tag>
            );
        }
      },
    },
    {
      title: 'Item & Requisition (PR)',
      key: 'prInfo',
      render: (_, rec) => (
        <div>
          <div style={{ fontWeight: 700, color: '#0F172A' }}>{rec.itemName}</div>
          <div style={{ fontSize: 11, color: '#475569' }}>
            <Tag color="purple" style={{ fontSize: 10, padding: '0 4px', lineHeight: '16px' }}>
              {rec.itemCode}
            </Tag>
            Qty Req: <strong>{rec.requestedQty.toLocaleString('id-ID')} {rec.uomCode}</strong>
          </div>
          <div style={{ marginTop: 4, fontSize: 11, color: '#64748B' }}>
            PR: <a onClick={() => router.push(`/purchase-requests/${rec.prId}`)} style={{ fontWeight: 600 }}>{rec.prNumber}</a>
            {' '}&bull; {rec.department} ({rec.requesterName})
          </div>
        </div>
      ),
    },
    {
      title: 'Order Commitment (PO)',
      key: 'poInfo',
      render: (_, rec) => {
        if (!rec.poNumber) {
          return (
            <Text type="secondary" italic style={{ fontSize: 12 }}>
              Awaiting PO Consolidation
            </Text>
          );
        }
        return (
          <div>
            <div>
              <a onClick={() => router.push(`/purchase-orders/${rec.poId}`)} style={{ fontWeight: 700, color: '#4F46E5' }}>
                {rec.poNumber}
              </a>
            </div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#1E293B' }}>
              {rec.supplierName}
            </div>
            <div style={{ fontSize: 11, color: '#64748B' }}>
              Ordered: <strong>{rec.orderedQty?.toLocaleString('id-ID')} {rec.uomCode}</strong>
              {rec.unitCost ? ` @ Rp ${rec.unitCost.toLocaleString('id-ID')}` : ''}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Receipt & Inspection (GR)',
      key: 'grInfo',
      render: (_, rec) => {
        if (!rec.grNumber) {
          return (
            <Text type="secondary" italic style={{ fontSize: 12 }}>
              Awaiting Goods Receipt
            </Text>
          );
        }
        return (
          <div>
            <div>
              <a onClick={() => router.push(`/goods-receipts/${rec.grId}`)} style={{ fontWeight: 700, color: '#0284C7' }}>
                {rec.grNumber}
              </a>
            </div>
            <div style={{ fontSize: 11, color: '#475569' }}>
              SJ / DO: <strong>{rec.vendorDeliveryNote}</strong>
            </div>
            <div style={{ fontSize: 12 }}>
              <span style={{ color: '#16A34A', fontWeight: 700 }}>
                Good: {rec.goodReceivedQty?.toLocaleString('id-ID')} {rec.uomCode}
              </span>
              {(rec.rejectedQty ?? 0) > 0 && (
                <span style={{ color: '#E11D48', fontWeight: 700, marginLeft: 8 }}>
                  Rej: {rec.rejectedQty?.toLocaleString('id-ID')}
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Inventory Cost Layer',
      key: 'costLayer',
      render: (_, rec) => {
        if (!rec.costLayerId) {
          return (
            <Text type="secondary" italic style={{ fontSize: 12 }}>
              No Cost Layer Created
            </Text>
          );
        }
        return (
          <div style={{ background: '#F8FAFC', padding: '6px 10px', borderRadius: 8, border: '1px solid #E2E8F0' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#4338CA' }}>
              <DatabaseOutlined style={{ marginRight: 4 }} />
              {rec.costLayerId}
            </div>
            <div style={{ fontSize: 11, color: '#334155' }}>
              On-hand Layer: <strong>{rec.remainingQty?.toLocaleString('id-ID')} {rec.uomCode}</strong>
            </div>
            <div style={{ fontSize: 11, color: '#059669', fontWeight: 600 }}>
              Layer Cost: Rp {rec.unitCost?.toLocaleString('id-ID')}
            </div>
          </div>
        );
      },
    },
  ];

  // Quality Report Columns
  const qualityColumns: ColumnsType<SupplierPerformanceReport> = [
    {
      title: 'Supplier Name',
      key: 'supplier',
      render: (_, s) => (
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#0F172A' }}>{s.supplierName}</div>
          <Space size={4}>
            <Tag color="blue" style={{ fontSize: 10 }}>{s.supplierCode}</Tag>
            {s.phone && <Text type="secondary" style={{ fontSize: 11 }}>{s.phone}</Text>}
          </Space>
        </div>
      ),
    },
    {
      title: 'Procurement Volume',
      key: 'volume',
      render: (_, s) => (
        <div>
          <div style={{ fontSize: 12 }}>
            Total Orders: <strong>{s.totalPOs} PO(s)</strong>
          </div>
          <div style={{ fontSize: 12 }}>
            Total Value: <strong>Rp {s.totalSpend.toLocaleString('id-ID')}</strong>
          </div>
          <div style={{ fontSize: 12, color: '#64748B' }}>
            Total Shipments: {s.totalGRs} receipt(s)
          </div>
        </div>
      ),
    },
    {
      title: 'Inspection Quantities',
      key: 'inspection',
      render: (_, s) => (
        <div>
          <div style={{ fontSize: 12, display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <Text type="secondary">Delivered Qty:</Text>
            <Text strong>{s.totalDeliveredQty.toLocaleString('id-ID')}</Text>
          </div>
          <div style={{ fontSize: 12, display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <Text type="secondary">Accepted (Good):</Text>
            <Text style={{ color: '#16A34A', fontWeight: 700 }}>
              {s.totalGoodQty.toLocaleString('id-ID')}
            </Text>
          </div>
          <div style={{ fontSize: 12, display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <Text type="secondary">Rejected / Damaged:</Text>
            <Text style={{ color: s.totalRejectedQty > 0 ? '#E11D48' : '#64748B', fontWeight: 700 }}>
              {s.totalRejectedQty.toLocaleString('id-ID')}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Quality Acceptance Rate',
      key: 'acceptance',
      width: 200,
      render: (_, s) => (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
            <span style={{ fontSize: 12, fontWeight: 600 }}>Quality Score</span>
            <span
              style={{
                fontSize: 13,
                fontWeight: 800,
                color: s.acceptanceRate >= 98 ? '#15803D' : s.acceptanceRate >= 90 ? '#D97706' : '#DC2626',
              }}
            >
              {s.acceptanceRate}%
            </span>
          </div>
          <Progress
            percent={s.acceptanceRate}
            size="small"
            status={s.acceptanceRate < 95 ? 'exception' : 'success'}
            strokeColor={
              s.acceptanceRate >= 98
                ? '#10B981'
                : s.acceptanceRate >= 90
                ? '#F59E0B'
                : '#EF4444'
            }
          />
          <div style={{ fontSize: 11, color: '#64748B', marginTop: 4 }}>
            {s.rejectionIncidentsCount === 0 ? (
              <span style={{ color: '#16A34A' }}>
                <CheckCircleOutlined style={{ marginRight: 4 }} /> Zero defect incidents
              </span>
            ) : (
              <span style={{ color: '#E11D48' }}>
                <WarningOutlined style={{ marginRight: 4 }} /> {s.rejectionIncidentsCount} defect incident(s) recorded
              </span>
            )}
          </div>
        </div>
      ),
    },
  ];

  return (
    <div style={{ width: '100%', paddingBottom: 40 }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div>
          <Title level={3} style={{ margin: 0, color: '#0F172A', fontWeight: 800 }}>
            Procurement Reports & Analytics
          </Title>
          <Text type="secondary" style={{ fontSize: 13 }}>
            Real-time executive tracking of outstanding orders, end-to-end traceability, and vendor quality audits.
          </Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchReports} style={{ borderRadius: 10 }}>
            Refresh
          </Button>
          <Button
            icon={<PrinterOutlined />}
            onClick={() => window.print()}
            style={{ borderRadius: 10 }}
          >
            Print / Export
          </Button>
          <Button
            type="primary"
            icon={<ShoppingOutlined />}
            onClick={() => router.push('/purchase-orders/consolidate')}
            style={{
              borderRadius: 10,
              background: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)',
              fontWeight: 600,
            }}
          >
            Consolidate PO
          </Button>
        </Space>
      </div>

      {/* Top Level Executive KPI Ribbon */}
      <Row gutter={[10, 10]} style={{ marginBottom: 20 }}>
        <Col xs={12} sm={12} lg={6}>
          <div
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #F5F3FF 100%)',
              padding: '12px 14px',
              borderRadius: 14,
              border: '1px solid #DDD6FE',
              boxShadow: PASTEL_THEME.boxShadowCard,
            }}
          >
            <div style={{ fontSize: 10.5, fontWeight: 700, color: '#6D28D9', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Total PO Spend
            </div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#1E1B4B', marginTop: 2, wordBreak: 'break-word' }}>
              Rp {(kpis?.totalSpend ?? 0).toLocaleString('id-ID')}
            </div>
            <div style={{ fontSize: 10.5, color: '#6B7280', marginTop: 2 }}>
              Across {kpis?.openPOsCount ?? 0} active PO(s)
            </div>
          </div>
        </Col>

        <Col xs={12} sm={12} lg={6}>
          <div
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF1F2 100%)',
              padding: '12px 14px',
              borderRadius: 14,
              border: '1px solid #FECDD3',
              boxShadow: PASTEL_THEME.boxShadowCard,
            }}
          >
            <div style={{ fontSize: 10.5, fontWeight: 700, color: '#BE123C', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Outstanding Exposure
            </div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#881337', marginTop: 2, wordBreak: 'break-word' }}>
              Rp {(kpis?.outstandingCommitmentsValue ?? 0).toLocaleString('id-ID')}
            </div>
            <div style={{ fontSize: 10.5, color: '#6B7280', marginTop: 2 }}>
              Pending goods arrival
            </div>
          </div>
        </Col>

        <Col xs={12} sm={12} lg={6}>
          <div
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #ECFDF5 100%)',
              padding: '12px 14px',
              borderRadius: 14,
              border: '1px solid #A7F3D0',
              boxShadow: PASTEL_THEME.boxShadowCard,
            }}
          >
            <div style={{ fontSize: 10.5, fontWeight: 700, color: '#047857', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Inventory Valuation
            </div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#064E3B', marginTop: 2, wordBreak: 'break-word' }}>
              Rp {(kpis?.totalInventoryValuation ?? 0).toLocaleString('id-ID')}
            </div>
            <div style={{ fontSize: 10.5, color: '#6B7280', marginTop: 2 }}>
              {kpis?.activeCostLayersCount ?? 0} active FIFO layers
            </div>
          </div>
        </Col>

        <Col xs={12} sm={12} lg={6}>
          <div
            style={{
              background: 'linear-gradient(135deg, #FFFFFF 0%, #FEF9C3 100%)',
              padding: '12px 14px',
              borderRadius: 14,
              border: '1px solid #FDE047',
              boxShadow: PASTEL_THEME.boxShadowCard,
            }}
          >
            <div style={{ fontSize: 10.5, fontWeight: 700, color: '#A16207', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Vendor Quality
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#713F12', marginTop: 2 }}>
              {kpis?.monthlyGRVolume?.acceptanceRate ?? 100}%
            </div>
            <div style={{ fontSize: 10.5, color: '#6B7280', marginTop: 2 }}>
              Quality acceptance rate
            </div>
          </div>
        </Col>
      </Row>

      {/* Filter Toolbar */}
      <Card
        variant="borderless"
        style={{
          borderRadius: 14,
          marginBottom: 20,
          background: '#FFFFFF',
          boxShadow: PASTEL_THEME.boxShadowCard,
        }}
      >
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={10}>
            <Input
              placeholder="Search by PO #, PR #, GR #, Item Code, or Name..."
              prefix={<FilterOutlined style={{ color: '#94A3B8' }} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              allowClear
              style={{ borderRadius: 10 }}
            />
          </Col>
          <Col xs={24} sm={12} md={7}>
            <Select
              style={{ width: '100%', borderRadius: 10 }}
              value={supplierFilter}
              onChange={setSupplierFilter}
              options={[
                { label: 'All Suppliers', value: 'ALL' },
                ...supplierQuality.map((s) => ({
                  label: `${s.supplierName} (${s.supplierCode})`,
                  value: s.supplierId,
                })),
              ]}
            />
          </Col>
          {activeTab === 'traceability' && (
            <Col xs={24} sm={12} md={7}>
              <Select
                style={{ width: '100%', borderRadius: 10 }}
                value={stageFilter}
                onChange={setStageFilter}
                options={[
                  { label: 'All Pipeline Stages', value: 'ALL' },
                  { label: 'Requisition Only (PR)', value: 'PR_STAGE' },
                  { label: 'Order Issued (PO)', value: 'PO_STAGE' },
                  { label: 'Goods Received (GR)', value: 'GR_STAGE' },
                  { label: 'Inventory Costed', value: 'COSTED_INVENTORY' },
                ]}
              />
            </Col>
          )}
        </Row>
      </Card>

      {/* Tabbed Reports */}
      <Card
        variant="borderless"
        style={{
          borderRadius: 14,
          background: '#FFFFFF',
          boxShadow: PASTEL_THEME.boxShadowCard,
        }}
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'outstanding-pos',
              label: (
                <span style={{ fontSize: 14, fontWeight: 600 }}>
                  <ShoppingOutlined style={{ marginRight: 8 }} />
                  Outstanding PO Report ({filteredOutstanding.length})
                </span>
              ),
              children: (
                <div>
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16, borderRadius: 10 }}
                    title={
                      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                        <span>
                          Tracking <strong>{filteredOutstanding.length}</strong> active line item commitments across suppliers.
                        </span>
                        <span>
                          Total Remaining Commitment:{' '}
                          <strong style={{ color: '#B91C1C' }}>
                            Rp {totalOutstandingValue.toLocaleString('id-ID')}
                          </strong>
                        </span>
                      </div>
                    }
                  />

                  <Table
                    columns={outstandingColumns}
                    dataSource={filteredOutstanding}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 8 }}
                    bordered={false}
                    scroll={{ x: 1000 }}
                  />
                </div>
              ),
            },
            {
              key: 'traceability',
              label: (
                <span style={{ fontSize: 14, fontWeight: 600 }}>
                  <BranchesOutlined style={{ marginRight: 8 }} />
                  End-to-End Traceability Ledger ({filteredTraceability.length})
                </span>
              ),
              children: (
                <div>
                  <Alert
                    type="success"
                    showIcon
                    style={{ marginBottom: 16, borderRadius: 10 }}
                    title="Full downstream audit trail connecting Purchase Requisition (PR) -> Consolidated PO -> Physical Goods Receipt (GR) -> FIFO Cost Layers."
                  />

                  <Table
                    columns={traceabilityColumns}
                    dataSource={filteredTraceability}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 8 }}
                    bordered={false}
                    scroll={{ x: 1000 }}
                  />
                </div>
              ),
            },
            {
              key: 'supplier-quality',
              label: (
                <span style={{ fontSize: 14, fontWeight: 600 }}>
                  <SafetyCertificateOutlined style={{ marginRight: 8 }} />
                  Supplier Performance & Quality Report ({filteredSuppliers.length})
                </span>
              ),
              children: (
                <div>
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16, borderRadius: 10 }}
                    title="Audit summary of vendor reliability, comparing delivered goods vs damaged/rejected quantities per shipment."
                  />

                  <Table
                    columns={qualityColumns}
                    dataSource={filteredSuppliers}
                    rowKey="supplierId"
                    loading={loading}
                    pagination={{ pageSize: 8 }}
                    bordered={false}
                    scroll={{ x: 1000 }}
                    expandable={{
                      expandedRowRender: (record) => {
                        if (record.incidents.length === 0) {
                          return (
                            <div style={{ padding: '8px 16px', color: '#16A34A', fontSize: 13 }}>
                              <CheckCircleOutlined style={{ marginRight: 6 }} /> No quality defects or rejected items recorded for this supplier.
                            </div>
                          );
                        }
                        return (
                          <div style={{ padding: '8px 16px', background: '#FFF1F2', borderRadius: 8 }}>
                            <Text strong style={{ color: '#9F1239', fontSize: 13 }}>
                              Quality Discrepancy & Rejection Log ({record.incidents.length} incidents):
                            </Text>
                            <Table
                              size="small"
                              rowKey={(inc) => `${inc.grId}-${inc.itemCode}`}
                              dataSource={record.incidents}
                              pagination={false}
                              style={{ marginTop: 8 }}
                              columns={[
                                { title: 'GR Slip', dataIndex: 'grNumber', key: 'grNumber', render: (val) => <Text strong>{val}</Text> },
                                { title: 'Date', dataIndex: 'receiveDate', key: 'receiveDate', render: (d) => new Date(d).toLocaleDateString('id-ID') },
                                { title: 'DO Note', dataIndex: 'vendorDeliveryNote', key: 'vendorDeliveryNote' },
                                { title: 'PO Ref', dataIndex: 'poNumber', key: 'poNumber' },
                                { title: 'Item', key: 'item', render: (_, inc) => `${inc.itemName} (${inc.itemCode})` },
                                { title: 'Accepted', dataIndex: 'goodQty', key: 'goodQty', render: (q, inc) => <Tag color="green">{q} {inc.uomCode}</Tag> },
                                { title: 'Rejected', dataIndex: 'rejectedQty', key: 'rejectedQty', render: (q, inc) => <Tag color="red">{q} {inc.uomCode}</Tag> },
                                { title: 'Rejection Notes', dataIndex: 'conditionNotes', key: 'conditionNotes', render: (n) => <Text italic type="secondary">{n || 'Damaged goods detected during inspection'}</Text> },
                              ]}
                            />
                          </div>
                        );
                      },
                      rowExpandable: () => true,
                    }}
                  />
                </div>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
}
